// src/components/roads/RoadTableRow.js
// --- UPDATE (v5.0 AI-connected row)
//   • AI RUL cell → AI prediction cell: remaining life (ML or labelled estimate),
//     risk index and data-readiness bar. Click it to expand the row.
//   • Expandable detail row: every road field, the explainable AI prediction
//     (why / what to do / what the AI uses / model insights) and the AiRoadHelper
//     checklist. Recommended actions call the existing row callbacks.
//   • Cells now show the data that was hidden: classification + surface,
//     width + lanes, condition score bar, inspection countdown / overdue, unmapped flag.
//   • Menu: "AI prediction & helper", "Refresh AI prediction", "Map on GIS".
//   • New optional props: onRunAI, onMapRoad, isRunningAI, columnCount, defaultExpanded.
//   • FIX: two `transition` props on the row — the CSS string overrode the
//     framer-motion stagger, so rows never staggered.
//   • FIX: road.status may be null → label fallback.
// --- END UPDATE
// --- UPDATE (Road Works report tab)
//   • Optional onOpenReport prop → "Works report" menu item opens the road in the page's
//     Road Works Report tab (segments, logistics, inspections, QA, work orders, AI).
// --- END UPDATE
import React, { memo, useState, useMemo, useCallback } from 'react'; // --- UPDATE: + useState/useMemo/useCallback
import {
    Box, Td, Text, HStack, Badge, Icon, Menu, MenuButton, MenuList, MenuItem,
    MenuDivider, IconButton, useColorModeValue, Tooltip,
    Tr, Collapse, SimpleGrid, Progress, Flex, // --- UPDATE
} from '@chakra-ui/react';
import {
    MdCheckBox, MdCheckBoxOutlineBlank, MdMoreVert, MdVisibility, MdEdit,
    MdAssessment, MdSchedule, MdBuild, MdReportProblem, MdDelete,
    MdFiberManualRecord, MdEngineering, MdWarning, MdError,
    MdExpandMore, MdChevronRight, MdOutlineAutoAwesome, MdMap, MdLocationOff, // --- UPDATE
    MdConstruction, // --- UPDATE: works report
} from 'react-icons/md';
import { motion } from 'framer-motion';

// 🚀 NEW: Import Watchdog logic
import { analyzeRoadCompliance } from './RoadSystemWatchdog';
// --- UPDATE: AI prediction + helper
import AiRoadHelper, { FieldHint } from './AiRoadHelper';
import { getRoadPrediction, ACTION_INTENTS } from './roadAiPredictions';
import { RoadAiPredictionBadge, RoadAiPredictionDetail } from './RoadAiPrediction';
// --- END UPDATE

const MotionBox = motion(Box);

// Local Animation Config for the row
const ANIM = {
    fadeInUp: { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } },
    stagger: (i, base = 0.02) => ({ delay: i * base }),
};

// FlowSense Mapping Constants
const CONDITION_CFG = {
    excellent: { color: 'green', label: 'Excellent' },
    good: { color: 'teal', label: 'Good' },
    fair: { color: 'yellow', label: 'Fair' },
    poor: { color: 'orange', label: 'Poor' },
    critical: { color: 'red', label: 'Critical' },
};

const STATUS_CFG = {
    operational: { color: 'green', label: 'Operational' },
    maintenance_required: { color: 'orange', label: 'Maint. Needed' },
    under_maintenance: { color: 'blue', label: 'In Progress' },
    closed: { color: 'red', label: 'Closed' },
    planned: { color: 'purple', label: 'Planned' },
    decommissioned: { color: 'gray', label: 'Decommissioned' },
};

// --- UPDATE: helpers for the richer cells + detail row
const DEFAULT_COLUMN_COUNT = 10;
const titleCase = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null);
const fmtDate = (d) => {
    if (!d) return null;
    const t = new Date(d);
    return Number.isFinite(t.getTime()) ? t.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
};
const daysUntil = (d) => {
    if (!d) return null;
    const t = new Date(d).getTime();
    return Number.isFinite(t) ? Math.ceil((t - Date.now()) / 86400000) : null;
};

/** All the road's data in one labelled grid (expanded row). */
const RoadFactsGrid = memo(({ road, prediction }) => {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const tile = useColorModeValue('gray.50', 'whiteAlpha.50');
    const n = prediction.normalized;
    const m = road.metadata || {};
    const facts = [
        ['Road code', n.road_code, 'road_code'],
        ['Constituency', n.constituency, 'constituency'],
        ['Classification', titleCase(n.classification), 'classification'],
        ['Surface', titleCase(n.surface_type), 'surface_type'],
        ['Length', n.length_m != null ? `${Math.round(n.length_m).toLocaleString()} m` : null, 'length_m'],
        ['Width', n.width_m != null ? `${n.width_m} m` : null, 'width_m'],
        ['Lanes', n.lanes, 'lanes'],
        ['Speed limit', n.speed_limit_kmh != null ? `${n.speed_limit_kmh} km/h` : null, 'speed_limit_kmh'],
        ['Coordinates', n.latitude != null && n.longitude != null ? `${n.latitude.toFixed(5)}, ${n.longitude.toFixed(5)}` : null, 'coordinates'],
        ['Elevation', n.elevation != null ? `${n.elevation} m` : null, 'elevation'],
        ['Installed / paved', fmtDate(n.install_date), 'install_date'],
        ['Criticality', titleCase(n.asset_criticality), 'asset_criticality'],
        ['Condition', n.condition_score != null ? `${n.condition_score}/100${n.condition_rating ? ` · ${titleCase(n.condition_rating)}` : ''}` : titleCase(n.condition_rating), 'condition'],
        ['Last inspection', fmtDate(n.last_inspection_date), 'inspection'],
        ['Next inspection', fmtDate(n.next_inspection_due), 'inspection'],
        ['GIS route', n.hasGeometry ? `Mapped (${n.polyline.length} pts)` : null, 'geometry'],
        ['Open work orders', String(n.open_wo), null],
        ['Notes', m.notes || null, null],
    ];
    return (
        <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={2}>
            {facts.map(([label, value, field]) => (
                <Box key={label} p={2} borderRadius="lg" bg={tile}>
                    <HStack spacing={0}>
                        <Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase">{label}</Text>
                        {field && <FieldHint field={field} />}
                    </HStack>
                    <Text fontSize="xs" fontWeight="700" noOfLines={2} color={value == null || value === '' ? 'orange.400' : undefined}>
                        {value == null || value === '' ? 'Not recorded' : value}
                    </Text>
                </Box>
            ))}
        </SimpleGrid>
    );
});
RoadFactsGrid.displayName = 'RoadFactsGrid';
// --- END UPDATE

const RoadTableRow = memo(({
    road, index, isSelected, onToggle, onView, onEdit, onCreateWO,
    onAssess, onScheduleInsp, onReportIssue, onDelete,
    // --- UPDATE: AI wiring (all optional)
    onRunAI, onMapRoad, isRunningAI = false, columnCount = DEFAULT_COLUMN_COUNT, defaultExpanded = false,
    // --- END UPDATE
    onOpenReport, // --- UPDATE: Road Works report tab (optional)
}) => {
    // Hooks at the top level
    const rowHover    = useColorModeValue('gray.50', 'gray.750');
    const rowSelected = useColorModeValue('blue.50', 'blue.900');
    const mutedColor  = useColorModeValue('gray.500', 'gray.400');
    const codeColor   = useColorModeValue('blue.600', 'blue.300');
    const dateColor   = useColorModeValue('gray.700', 'white');
    // --- UPDATE
    const detailBg    = useColorModeValue('purple.50', 'blackAlpha.300');
    const detailBorder = useColorModeValue('purple.100', 'whiteAlpha.200');
    const trackBg     = useColorModeValue('gray.100', 'whiteAlpha.200');
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    // --- END UPDATE

    // Parse statuses
    const cond = CONDITION_CFG[road.metadata?.condition_rating] || { color: 'gray', label: 'Unrated' };
    const stat = STATUS_CFG[road.status] || { color: 'gray', label: titleCase(road.status) || 'Unknown' };

    // Check if the road has active work orders
    const openWOCount = road.open_work_orders_count || 0;
    const hasActiveWO = openWOCount > 0;

    // Run the road through the Watchdog
    // --- UPDATE: memoised (was recomputed on every parent render)
    const compliance = useMemo(() => analyzeRoadCompliance(road), [road]);
    const prediction = useMemo(() => getRoadPrediction(road), [road]);
    const toggleExpanded = useCallback(() => setIsExpanded(v => !v), []);

    const handleAction = useCallback((intent) => {
        switch (intent) {
            case ACTION_INTENTS.CREATE_WO: return onCreateWO?.(road);
            case ACTION_INTENTS.SCHEDULE_INSPECTION: return onScheduleInsp?.(road);
            case ACTION_INTENTS.ASSESS: return onAssess?.(road);
            case ACTION_INTENTS.EDIT: return onEdit?.(road);
            case ACTION_INTENTS.MAP: return onMapRoad ? onMapRoad(road) : onEdit?.(road);
            case ACTION_INTENTS.RUN_AI: return onRunAI?.(road);
            default: return onView?.(road);
        }
    }, [road, onCreateWO, onScheduleInsp, onAssess, onEdit, onMapRoad, onRunAI, onView]);

    const n = prediction.normalized;
    const inspDays = daysUntil(road.metadata?.next_inspection_due);
    const specLine = [titleCase(n.classification), titleCase(n.surface_type)].filter(Boolean).join(' · ');
    const sizeLine = [n.width_m != null && `${n.width_m} m wide`, n.lanes != null && `${n.lanes} lane${n.lanes === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
    // --- END UPDATE

    return (
        // --- UPDATE: fragment so the expandable detail row can follow
        <>
        <MotionBox as="tr"
            {...ANIM.fadeInUp} transition={ANIM.stagger(index, 0.02)}
            display="table-row"
            bg={isSelected ? rowSelected : 'transparent'}
            _hover={{ bg: isSelected ? rowSelected : rowHover }}
            // --- UPDATE: CSS transition moved to sx (a 2nd `transition` prop overrode the stagger)
            sx={{ transition: 'background 0.15s' }}
            aria-expanded={isExpanded}
            // --- END UPDATE
        >
            <Td>
                {/* --- UPDATE: expand chevron next to the checkbox */}
                <HStack spacing={1}>
                <MotionBox
                    animate={isSelected ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ duration: 0.3 }}
                    onClick={() => onToggle(road.id)}
                    cursor="pointer"
                >
                    <Icon as={isSelected ? MdCheckBox : MdCheckBoxOutlineBlank}
                        boxSize={4} color={isSelected ? 'blue.500' : 'gray.400'} />
                </MotionBox>
                <IconButton size="xs" variant="ghost" minW="auto" h="auto" p={0.5}
                    aria-label={isExpanded ? `Hide AI details for ${road.name}` : `Show AI details for ${road.name}`}
                    icon={<Icon as={isExpanded ? MdExpandMore : MdChevronRight} boxSize={4} />}
                    onClick={toggleExpanded} />
                </HStack>
                {/* --- END UPDATE */}
            </Td>

            <Td fontWeight="bold" fontFamily="mono" color={codeColor} fontSize="xs">
                {road.metadata?.road_code || '—'}
            </Td>

            <Td>
                <HStack spacing={3}>
                    <Box>
                        <HStack>
                            <Text fontWeight="bold" fontSize="sm" noOfLines={1}>{road.name}</Text>

                            {/* Hardhat Badge for Active Work Orders */}
                            {hasActiveWO && (
                                <Tooltip label={`${openWOCount} Active Work Order(s)`} hasArrow placement="top">
                                    <Badge colorScheme="purple" variant="solid" borderRadius="full" px={2} py={0.5} display="flex" alignItems="center" gap={1} cursor="help">
                                        <Icon as={MdEngineering} boxSize={3} />
                                        <Text fontSize="2xs">{openWOCount}</Text>
                                    </Badge>
                                </Tooltip>
                            )}

                            {/* Watchdog ML Data Starvation Warning */}
                            {!compliance.isReadyForML && (
                                <Tooltip label={`Missing ML Data: ${(compliance.missingMLData || []).join(', ')}`} hasArrow placement="top">
                                    <Box color="orange.500" cursor="help" mt="2px">
                                        <Icon as={MdWarning} boxSize={4} />
                                    </Box>
                                </Tooltip>
                            )}

                            {/* Watchdog SLA Overdue Warning */}
                            {compliance.isInspectionOverdue && (
                                <Tooltip label={`Inspection overdue by ${compliance.daysOverdue} days!`} hasArrow placement="top">
                                    <Box color="red.500" cursor="help" mt="2px">
                                        <Icon as={MdError} boxSize={4} />
                                    </Box>
                                </Tooltip>
                            )}

                            {/* --- UPDATE: unmapped flag */}
                            {!prediction.hasGeometry && (
                                <Tooltip label="Not drawn on the GIS map — excluded from the risk heatmap and pothole clustering" hasArrow placement="top">
                                    <Box color="gray.400" cursor="help" mt="2px">
                                        <Icon as={MdLocationOff} boxSize={4} aria-label="Unmapped" />
                                    </Box>
                                </Tooltip>
                            )}
                            {/* --- END UPDATE */}
                        </HStack>
                        <Text fontSize="2xs" color={mutedColor} noOfLines={1} maxW="240px">
                            {road.location_description || '—'}
                        </Text>
                    </Box>
                </HStack>
            </Td>

            <Td fontSize="xs">
                {road.metadata?.constituency || '—'}
                {/* --- UPDATE: classification + surface */}
                {specLine && <Text fontSize="2xs" color={mutedColor} noOfLines={1}>{specLine}</Text>}
                {/* --- END UPDATE */}
            </Td>

            <Td>
                <HStack spacing={1.5}>
                    <Box w={2} h={2} borderRadius="full" bg={`${cond.color}.500`}
                        boxShadow={`0 0 6px var(--chakra-colors-${cond.color}-400)`} />
                    <Badge colorScheme={cond.color} fontSize="2xs" borderRadius="full">
                        {cond.label}
                    </Badge>
                    {road.metadata?.condition_score != null && (
                        <Text fontSize="2xs" color={mutedColor} fontFamily="mono">
                            {road.metadata.condition_score}
                        </Text>
                    )}
                </HStack>
                {/* --- UPDATE: condition score bar */}
                {n.condition_score != null && (
                    <Progress value={n.condition_score} size="xs" mt={1} w="90px" borderRadius="full" bg={trackBg}
                        colorScheme={cond.color === 'gray' ? 'blue' : cond.color} aria-label={`Condition ${n.condition_score} of 100`} />
                )}
                {/* --- END UPDATE */}
            </Td>

            {/* 🚀 UPDATED: AI RUL Score with Contextual Tooltips */}
            {/* --- UPDATE: full AI prediction cell (RUL source, risk index, readiness) — click to expand */}
            <Td isNumeric fontSize="xs" fontFamily="mono">
                <RoadAiPredictionBadge prediction={prediction} onClick={toggleExpanded} />
            </Td>
            {/* --- END UPDATE */}

            <Td isNumeric fontSize="xs" fontFamily="mono">
                {road.metadata?.length_m ? Number(road.metadata.length_m).toFixed(0) : '—'}
                {/* --- UPDATE: width / lanes */}
                {sizeLine && <Text fontSize="2xs" color={mutedColor} fontFamily="body">{sizeLine}</Text>}
                {/* --- END UPDATE */}
            </Td>

            <Td>
                {road.metadata?.next_inspection_due ? (
                    <HStack spacing={1.5}>
                        <Icon as={MdSchedule} color={compliance.isInspectionOverdue ? 'red.500' : 'cyan.500'} boxSize={3.5} />
                        <Text fontSize="xs" fontWeight="600" color={compliance.isInspectionOverdue ? 'red.500' : dateColor}>
                            {new Date(road.metadata.next_inspection_due).toLocaleDateString(undefined, {
                                month: 'short', day: 'numeric', year: 'numeric'
                            })}
                        </Text>
                    </HStack>
                ) : (
                    <Text fontSize="xs" color={mutedColor}>Unscheduled</Text>
                )}
                {/* --- UPDATE: countdown */}
                {inspDays != null && (
                    <Text fontSize="2xs" color={inspDays < 0 ? 'red.400' : inspDays <= 30 ? 'orange.400' : mutedColor}>
                        {inspDays < 0 ? `${Math.abs(inspDays)}d overdue` : inspDays === 0 ? 'Due today' : `in ${inspDays}d`}
                    </Text>
                )}
                {/* --- END UPDATE */}
            </Td>

            <Td>
                <Badge colorScheme={stat.color} variant="subtle" borderRadius="full" px={2} fontSize="2xs">
                    {stat.label}
                </Badge>
            </Td>

            <Td>
                <Menu isLazy>
                    <MenuButton as={IconButton} icon={<Icon as={MdMoreVert} />}
                        size="xs" variant="ghost" borderRadius="full" aria-label={`Actions for ${road.name}`} />
                    <MenuList borderRadius="xl" boxShadow="xl" fontSize="sm" zIndex={10}>
                        <MenuItem icon={<Icon as={MdVisibility} />} onClick={() => onView(road)}>
                            View Details
                        </MenuItem>
                        {/* --- UPDATE: Road Works report tab */}
                        {onOpenReport && (
                            <MenuItem icon={<Icon as={MdConstruction} color="blue.400" />} onClick={() => onOpenReport(road)}>
                                Works report
                            </MenuItem>
                        )}
                        {/* --- END UPDATE */}
                        {/* --- UPDATE */}
                        <MenuItem icon={<Icon as={MdOutlineAutoAwesome} color="purple.400" />} onClick={toggleExpanded}>
                            {isExpanded ? 'Hide AI prediction & helper' : 'AI prediction & helper'}
                        </MenuItem>
                        {onRunAI && (
                            <MenuItem icon={<Icon as={MdOutlineAutoAwesome} />} onClick={() => onRunAI(road)} isDisabled={isRunningAI}>
                                Refresh AI prediction
                            </MenuItem>
                        )}
                        {onMapRoad && !prediction.hasGeometry && (
                            <MenuItem icon={<Icon as={MdMap} />} onClick={() => onMapRoad(road)}>
                                Map on GIS
                            </MenuItem>
                        )}
                        {/* --- END UPDATE */}
                        <MenuItem icon={<Icon as={MdEdit} />} onClick={() => onEdit(road)}>
                            Edit Road Data
                        </MenuItem>
                        <MenuItem icon={<Icon as={MdAssessment} />} onClick={() => onAssess(road)}>
                            Record Condition
                        </MenuItem>
                        <MenuItem icon={<Icon as={MdSchedule} />} onClick={() => onScheduleInsp(road)}>
                            Schedule Inspection
                        </MenuItem>
                        <MenuDivider />
                        <MenuItem icon={<Icon as={MdBuild} />} onClick={() => onCreateWO(road)}>
                            Create Work Order
                        </MenuItem>
                        <MenuItem icon={<Icon as={MdReportProblem} />} onClick={() => onReportIssue(road)}>
                            Report Issue
                        </MenuItem>
                        <MenuDivider />
                        <MenuItem icon={<Icon as={MdDelete} />} color="red.500" onClick={() => onDelete(road)}>
                            Delete Road
                        </MenuItem>
                    </MenuList>
                </Menu>
            </Td>
        </MotionBox>

        {/* --- UPDATE: expandable AI detail row (rendered only when open) */}
        {isExpanded && (
            <Tr bg={`${detailBg} !important`} _hover={{ bg: `${detailBg} !important` }}>
                <Td colSpan={columnCount} p={0} borderBottomWidth="2px" borderColor={detailBorder}>
                    <Collapse in animateOpacity>
                        <Box p={4}>
                            <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
                                <HStack spacing={2}>
                                    <Icon as={MdOutlineAutoAwesome} color="purple.400" />
                                    <Text fontWeight="800" fontSize="sm">AI analysis · {road.name}</Text>
                                    {road.metadata?.road_code && <Badge fontFamily="mono">{road.metadata.road_code}</Badge>}
                                </HStack>
                                <HStack spacing={1}>
                                    <Icon as={MdFiberManualRecord} boxSize={2} color={prediction.source === 'model' ? 'purple.400' : 'gray.400'} />
                                    <Text fontSize="2xs" color={mutedColor}>
                                        {prediction.source === 'model' ? 'Prediction from the FlowSense ML service'
                                            : prediction.source === 'estimate' ? 'Rule-based estimate — no ML prediction yet'
                                            : 'Not enough data for a prediction'}
                                    </Text>
                                </HStack>
                            </Flex>
                            <SimpleGrid columns={{ base: 1, xl: 5 }} spacing={4}>
                                <Box gridColumn={{ xl: 'span 3' }}>
                                    <RoadAiPredictionDetail prediction={prediction} onAction={handleAction} isRunningAI={isRunningAI} />
                                </Box>
                                <Box gridColumn={{ xl: 'span 2' }}>
                                    <AiRoadHelper road={road} context="view" variant="compact" defaultOpen
                                        onFix={() => onEdit?.(road)} />
                                </Box>
                            </SimpleGrid>
                            <Box mt={4}>
                                <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={mutedColor} mb={2}>All road data</Text>
                                <RoadFactsGrid road={road} prediction={prediction} />
                            </Box>
                        </Box>
                    </Collapse>
                </Td>
            </Tr>
        )}
        {/* --- END UPDATE */}
        </>
    );
});

export default RoadTableRow;
